import { isPredictableUpdate } from "@common/api/quoting/optimistic-quote"
import { useOptimisticQuoteUpdateMutation } from "@common/api/quoting/quote"
import type { QuotePatchResult, UpdateQuote } from "@common/client"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { handleError } from "@common/utils"
import useCustomToast from "@/hooks/use-custom-toast"

export type QuoteUpdate = (
  body: UpdateQuote,
  onSaved?: (result: QuotePatchResult) => void,
) => void

export function useQuoteUpdate(quoteId: string): {
  update: QuoteUpdate
  pending: boolean
} {
  const showToast = useCustomToast()
  const trackEvent = useTrackEvent()
  const mutation = useOptimisticQuoteUpdateMutation(quoteId, {
    onError: (err) => handleError(err, showToast),
  })

  const update: QuoteUpdate = (body, onSaved) =>
    mutation.mutate(
      { quote_id: quoteId, updateQuote: body },
      {
        onSuccess: (result) => {
          let createdIndex = 0
          for (const operation of body.tasks ?? []) {
            switch (operation.operation) {
              case "create":
              case "create_from_template":
                trackEvent("quote_task_added", {
                  quote_id: quoteId,
                  quote_task_id: result.created_task_ids[createdIndex],
                })
                createdIndex += 1
                break
              case "remove":
                trackEvent("quote_task_removed", {
                  quote_id: quoteId,
                  quote_task_id: operation.id,
                })
                break
              case "update":
                trackEvent("quote_task_edited", {
                  quote_id: quoteId,
                  quote_task_id: operation.id,
                  fields_changed: Object.keys(operation).filter(
                    (field) => field !== "operation" && field !== "id",
                  ),
                })
                break
              case "apply_template":
                trackEvent("quote_task_edited", {
                  quote_id: quoteId,
                  quote_task_id: operation.id,
                  fields_changed: ["canned_work_task_id"],
                })
                break
            }
          }
          const quoteFields = Object.keys(body).filter(
            (field) => field !== "tasks",
          )
          if (quoteFields.length > 0) {
            trackEvent("quote_edited", {
              quote_id: quoteId,
              fields_changed: quoteFields,
            })
          }
          onSaved?.(result)
        },
      },
    )

  return {
    update,
    pending:
      mutation.isPending &&
      !isPredictableUpdate(mutation.variables.updateQuote),
  }
}

export function useConfirmSaved(): () => void {
  const showToast = useCustomToast()
  return () => showToast("Task updated", "", "success")
}
